import { FormattedMessage } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import Chip from '@mui/material/Chip';
import { StaffViewInfo } from '@libs/models/talents/talent';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  LinearProgress,
  Link,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useViewProviderInfoStore } from '@libs/store/talents/talentViewStore';
import { Divider, PrimaryPaperBlock } from '@atoms/layout';
import { Button } from '@atoms/buttons';
import { AgencyStaffAvatar, FacilityStaffAvatar, OrganizationAvatar } from '@atoms/dataDisplay';
import { FaIcon } from '@atoms/icons';
import { useLocationStore } from '@libs/store/locations';
import { updateExternalStaff } from '@libs/api/talents';
import { useRefreshSyncStore } from '@libs/store/refreshSync';
import { useState } from 'react';

export function TalentViewDrawer() {
  const { closeDrawer, open, loading, data } = useViewProviderInfoStore();

  return (
    <Drawer open={open} onClose={closeDrawer} anchor="right">
      {loading ? (
        <Stack width={{ xs: '300px', sm: '600px' }} height="100%" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Stack>
      ) : (
        <TalentViewPage talentData={data} />
      )}
    </Drawer>
  );
}

type TalentEditFormProps = {
  talentData?: StaffViewInfo | null;
};
function TalentViewPage({ talentData }: TalentEditFormProps) {
  const { current } = useLocationStore();
  const { load } = useViewProviderInfoStore();
  const { callRefresh } = useRefreshSyncStore();
  const [isOrientedUpdating, setIsOrientedUpdating] = useState<boolean>(false);
  const theme = useTheme();

  const allowToOperate = false;

  const talent = talentData;

  const handleOpenEdit = () => {};

  const handleOrientedChange = async (value: boolean) => {
    if (talent && current) {
      setIsOrientedUpdating(true);
      await updateExternalStaff(talent.id, {
        oriented: {
          locationId: current.id,
          value,
        },
      });
      await load(talent.id, talent.type, true);
      setIsOrientedUpdating(false);
      await callRefresh();
    }
  };

  if (!talent) {
    return (
      <Stack width={{ xs: '300px', sm: '600px' }} height="100%" justifyContent="center" alignItems="center">
        <Typography variant="h6">
          <FormattedMessage id="talents.viewDrawer.noData" />
        </Typography>
      </Stack>
    );
  }

  const positionsSet = talent.qualifications.map((qualification, index) => (
    <Tooltip key={index} title={'Position'}>
      <Chip key={index} label={qualification.qualification.name} sx={{ cursor: 'default' }} />
    </Tooltip>
  ));
  const specialtiesSet = talent.qualifications.reduce<React.ReactNode[]>((acc, qualification, index) => {
    qualification.specialties.map((s, sidx) => {
      acc.push(
        <Tooltip key={`${index}-${sidx}`} title={'Specialty'}>
          <Chip key={`${index}-${sidx}`} label={s.name} sx={{ cursor: 'default' }} />
        </Tooltip>,
      );
    });
    return acc;
  }, []);

  return (
    <Stack direction="column" height="100%">
      <Stack direction="column" width={{ xs: '300px', sm: '600px' }} px={2} height="100%" sx={{ overflowY: 'auto' }}>
        <PrimaryPaperBlock sx={{ pt: 3 }}>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              {talent.type === 'internal' ? (
                <Box>
                  <FacilityStaffAvatar
                    src={talent.imageUrl}
                    fullName={talent.fullName}
                    sx={{ width: '64px', height: '64px' }}
                  />
                </Box>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <AgencyStaffAvatar
                    src={talent.imageUrl}
                    fullName={talent.fullName}
                    sx={{ width: '64px', height: '64px' }}
                  />
                  <Tooltip title={talent.agency?.name}>
                    <OrganizationAvatar
                      src={talent.agency?.logoUrl}
                      fullName={talent.agency?.name}
                      sx={{
                        position: 'absolute',
                        bottom: -10,
                        right: -5,
                        width: '30px',
                        height: '30px',
                        border: `2px solid ${theme.palette.warning.main}`,
                      }}
                    />
                  </Tooltip>
                </Box>
              )}
              <Stack direction="column" spacing={0}>
                <Typography variant="h5">{talent.fullName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {talent.jobTitle}
                </Typography>
                <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
                  <Chip label={'Provider'} variant="outlined" color="secondary" size="small" />
                  {talent.employmentType !== 'Agency' ? (
                    <Chip label={talent.employmentType} variant="outlined" color="info" size="small" />
                  ) : (
                    <Chip label={'External'} variant="outlined" color="warning" size="small" />
                  )}
                  {talent.contractType && (
                    <Chip label={talent.contractType} variant="outlined" color="info" size="small" />
                  )}
                </Stack>
              </Stack>
            </Stack>
            <Grid container pt={2}>
              <Grid item xs={6}>
                <Stack direction="column" spacing={1}>
                  {talent.email && (
                    <Stack direction={'row'} spacing={1} alignItems="center">
                      <FaIcon name="envelope" />
                      <Link href={`mailto:${talent.email}`} underline="hover">
                        <Tooltip title={talent.email}>
                          <Typography
                            variant="body2"
                            sx={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {talent.email}
                          </Typography>
                        </Tooltip>
                      </Link>
                    </Stack>
                  )}
                  {talent.phoneNumber && (
                    <Stack direction={'row'} spacing={1} alignItems="center">
                      <FaIcon name="phone" />
                      <Link href={`tel:${talent.phoneNumber}`} underline="hover">
                        <Tooltip title={talent.phoneNumber}>
                          <Typography
                            variant="body2"
                            sx={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {talent.phoneNumber}
                          </Typography>
                        </Tooltip>
                      </Link>
                    </Stack>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={6}>
                {talent.address && (
                  <Stack direction={'row'} spacing={1} alignItems="center">
                    <FaIcon name="address-book" />
                    <Stack direction="column">
                      <Typography variant="body2">
                        {talent.address?.firstAddress}
                        {/* {talent.address?.city}, {talent.address?.state}{' '}
                        {talent.address?.zipCode} */}
                      </Typography>
                      {talent.address?.secondAddress && (
                        <Typography variant="body2" color="text.disabled" sx={{ cursor: 'default' }}>
                          {talent.address?.secondAddress}
                          {talent.address?.city}, {talent.address?.state} {talent.address?.zipCode}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                )}
              </Grid>
            </Grid>
            {talent.location && (
              <Stack direction={'row'} spacing={1} alignItems="center">
                <FaIcon name="map-location-dot" />
                <Tooltip title="Working location">
                  <Typography
                    variant="body2"
                    sx={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default' }}
                  >
                    {talent.location?.name}
                  </Typography>
                </Tooltip>
              </Stack>
            )}
            {talent.hrInfo && (
              <Stack direction={'row'} spacing={1} alignItems="center" pl={3.5}>
                <Tooltip title="Primary region">
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: '250px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 500,
                      cursor: 'default',
                    }}
                  >
                    {talent.hrInfo?.primaryRegion.city}, {talent.hrInfo?.primaryRegion.state}
                  </Typography>
                </Tooltip>
                {!!talent.hrInfo?.secondaryRegions.length && (
                  <Tooltip title="Secondary regions">
                    <Box>
                      {talent.hrInfo?.secondaryRegions.map((region, index) => (
                        <Typography variant="body2" ml={2} key={index}>
                          {region.city}, {region.state}
                        </Typography>
                      ))}
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Stack>
        </PrimaryPaperBlock>
        {talent.type === 'external' && (
          <PrimaryPaperBlock blockTitle={<FormattedMessage id="talents.editForm.sections.oriented.heading" />}>
            {isOrientedUpdating ? <LinearProgress sx={{ height: '1px' }} /> : <Box sx={{ height: '1px' }} />}
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!talent.orientedList.find((o) => o.locationId === current?.id && o.value)}
                  onChange={(e) => !isOrientedUpdating && handleOrientedChange(e.target.checked)}
                />
              }
              label={
                <FormattedMessage
                  id="talents.editForm.sections.oriented.toggle.label"
                  values={{ locationName: current?.name || '' }}
                />
              }
            />
            <FormHelperText>
              <FormattedMessage
                id="talents.editForm.sections.oriented.helper"
                values={{ locationName: current?.name || '' }}
              />
            </FormHelperText>
          </PrimaryPaperBlock>
        )}
        <PrimaryPaperBlock blockTitle={<FormattedMessage id="talents.editForm.qualificationAndSpecialties" />}>
          {positionsSet}
          {!!specialtiesSet.length && <Divider sx={{ my: 1 }} />}
          {!!specialtiesSet.length && specialtiesSet}
        </PrimaryPaperBlock>
        {/* {talent.workShifts && (
          <PrimaryPaperBlock blockTitle={<FormattedMessage id="talents.editForm.schedule" />}></PrimaryPaperBlock>
        )} */}
        <PrimaryPaperBlock blockTitle={<FormattedMessage id="talents.drawers.editTalent.tabs.credentials.label" />}>
          {talent.credentials.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage id="talents.viewDrawer.noCredentials" />
            </Typography>
          ) : (
            <Stack direction="column" spacing={2}>
              {talent.credentials.map((credential, idx) => (
                <Box key={idx}>
                  <Stack direction="row" spacing={1} width={'100%'} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {credential.type}
                    </Typography>
                    <Box flex={1} />
                    <Typography variant="caption" color="text.secondary">
                      {credential.name}
                    </Typography>
                  </Stack>
                  {credential.url.endsWith('.pdf') ? (
                    <Link href={credential.url} target="_blank" rel="noopener" underline="hover">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title={credential.name}>
                          <Typography
                            variant="body2"
                            sx={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {credential.name}
                          </Typography>
                        </Tooltip>
                      </Stack>
                    </Link>
                  ) : (
                    <Tooltip title={credential.name}>
                      <Box>
                        <img
                          src={credential.url}
                          alt={credential.name}
                          style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', marginTop: '4px' }}
                        />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </PrimaryPaperBlock>
      </Stack>
      {allowToOperate && (
        <Box sx={{ position: 'sticky', bottom: 0, background: theme.palette.background.paper }}>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={2} mb={1} px={2} alignContent={'center'}>
            <Box flex={1} />
            <Button variant="passive" color="primary" size="large" onClick={handleOpenEdit}>
              <FormattedMessage id="common.edit" />
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
